<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { useGetInteractions } from '../hooks/interaction';
    import { useRoute } from 'vue-router';
    import { __ } from '../stores/expressions';
    import InteractionInfo from '../components/interactions/summary/InteractionInfo.vue';
    import LoadingDots from '../components/LoadingDots.vue';
    import PageHeader from '../components/PageHeader.vue';
    import EchoButton from '../components/EchoButton.vue';
    import TranscriptionChat from '../components/interactions/transcription/TranscriptionChat.vue';
    import CoachInteraction from '../components/quality-assurance/interaction/CoachInteraction.vue';
    import TranscriptionVisualization from '../components/interactions/transcription/TranscriptionVisualization.vue';
    import { ChevronLeftIcon, ChevronRightIcon, CircleCheckIcon } from '@lucide/vue';

    const route = useRoute();

    const req = computed(() => [route.params.interactionId as string]);

    const { data: interactions, isLoading } = useGetInteractions(req);

    const interaction = computed(() => {
        return interactions.value?.interactions[0];
    });

    const showTranscription = ref<boolean>(false);

    const currentTime = ref(0);
</script>
<template>
    <div class="page-padding flex flex-col gap-2">
        <PageHeader>
            <template #icon><CircleCheckIcon class="size-full" /></template>
            {{ __('quality-assurance') }}
        </PageHeader>
        <template v-if="!isLoading && interaction">
            <div class="Card flex flex-col gap-3">
                <div class="bg-gray-lightest rounded-md p-4">
                    <InteractionInfo :interaction="interaction" />
                </div>
                <div class="border border-gray-lightest rounded-md p-4">
                    <TranscriptionVisualization
                        :interaction="interaction"
                        @current-time-update="currentTime = $event"
                    />
                </div>
                <div class="border border-gray-lightest rounded-md">
                    <div class="px-12 py-8">
                        <div class="flex stretch h-full">
                            <div class="flex-3">
                                <div class="flex items-center justify-between h-8">
                                    <h2 class="text-overline">
                                        {{ __('quality-assurance-coach-interaction-choose-form') }}
                                    </h2>
                                    <EchoButton
                                        v-if="!showTranscription"
                                        variant="tertiary"
                                        @click="showTranscription = true"
                                    >
                                        <template #icon-left><ChevronLeftIcon /></template>
                                        {{ __('transcription') }}
                                    </EchoButton>
                                </div>
                                <div :class="showTranscription && 'pr-8'">
                                    <CoachInteraction
                                        :interaction-id="interaction.id"
                                        :full-width="!showTranscription"
                                    />
                                </div>
                            </div>
                            <div
                                v-if="showTranscription"
                                class="flex-2 border-gray-lightest border-l"
                            >
                                <div class="px-6 mb-5">
                                    <EchoButton
                                        variant="tertiary"
                                        @click="showTranscription = false"
                                    >
                                        {{ __('hide-transcription') }}
                                        <template #icon-right><ChevronRightIcon /></template>
                                    </EchoButton>
                                </div>
                                <div class="pl-8 block sticky top-0 left-0 -mr-8">
                                    <TranscriptionChat
                                        :interaction="interaction"
                                        :current-time="currentTime"
                                        :enable-keyword-filter="false"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="bg-white rounded-lg p-8 flex justify-center h-full">
                <LoadingDots />
            </div>
        </template>
    </div>
</template>
